import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Truck, MapPin, LogOut, Clock, Camera, History, 
  ChevronLeft, ChevronRight, CheckCircle, AlertCircle,
  FileText, RefreshCw, Image as ImageIcon, X, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format date as dd/mm/yyyy
const formatDateDMY = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Format time as HH:mm
const formatTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Format date with time as dd/mm/yyyy HH:mm
const formatDateTimeDMY = (dateString) => {
  if (!dateString) return '-';
  return `${formatDateDMY(dateString)} ${formatTime(dateString)}`;
};

export default function OperatorDashboard({ user, onLogout }) {
  const [assignedGrounds, setAssignedGrounds] = useState([]);
  const [selectedGroundId, setSelectedGroundId] = useState("");
  const [activeLog, setActiveLog] = useState(null);
  const [showCheckinForm, setShowCheckinForm] = useState(false); // Control check-in form visibility
  const [todayLogs, setTodayLogs] = useState([]);
  const [todayTotals, setTodayTotals] = useState([]);
  const [lorryNumber, setLorryNumber] = useState("");
  const [doPhoto, setDoPhoto] = useState(null);
  const [doPhotoPreview, setDoPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [viewingPhoto, setViewingPhoto] = useState(null); // For viewing DO photos
  const [photoLoading, setPhotoLoading] = useState(false);
  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    fetchDashboardData();
    // Poll every 10 seconds for updates
    const interval = setInterval(fetchDashboardData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/operator/dashboard`);
      setAssignedGrounds(response.data.assigned_grounds || []);
      setTodayLogs(response.data.today_logs || []);
      setTodayTotals(response.data.today_totals || []);
      
      // Auto-select first ground if only one assigned
      if (response.data.assigned_grounds?.length === 1 && !selectedGroundId) {
        setSelectedGroundId(response.data.assigned_grounds[0].id);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setDataLoading(false);
    }
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressedImage = await compressImage(file, 200); // Compress to max 200KB
        setDoPhoto(compressedImage);
        setDoPhotoPreview(compressedImage);
        toast.success("Photo captured & compressed");
      } catch (error) {
        toast.error("Failed to process image");
        console.error("Image compression error:", error);
      }
    }
  };

  // Compress image to target size (200KB max)
  const compressImage = (file, maxSizeKB) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image(); // Use window.Image to avoid conflict with lucide-react
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Start with smaller dimensions for better compression
          const maxDimension = 1000;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Start with quality 0.7 and reduce until under 200KB
          let quality = 0.7;
          let dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // Base64 adds ~37% overhead, so target size in base64 is maxSizeKB * 1.37 * 1024
          const targetBase64Size = maxSizeKB * 1024 * 1.37;
          
          // Iteratively reduce quality until we're under the limit
          while (dataUrl.length > targetBase64Size && quality > 0.1) {
            quality -= 0.05;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          
          // If still too large, reduce dimensions further
          if (dataUrl.length > targetBase64Size) {
            const scale = 0.7;
            canvas.width = Math.round(width * scale);
            canvas.height = Math.round(height * scale);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            dataUrl = canvas.toDataURL('image/jpeg', 0.6);
          }
          
          console.log(`Compressed image: ${Math.round(dataUrl.length / 1024)}KB (quality: ${quality.toFixed(2)})`);
          resolve(dataUrl);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    
    if (!selectedGroundId) {
      toast.error("Please select a dumping ground");
      return;
    }
    if (!lorryNumber.trim()) {
      toast.error("Please enter the lorry number");
      return;
    }
    if (!doPhoto) {
      toast.error("Please take a photo of the Delivery Order");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/dumping-logs/checkin`, {
        dumping_ground_id: selectedGroundId,
        lorry_number: lorryNumber.trim(),
        do_photo: doPhoto
      });
      
      setActiveLog(response.data);
      setShowCheckinForm(false); // Hide form and show confirmation
      toast.success(`Lorry ${lorryNumber} checked in successfully!`);
      
      // Reset form
      setLorryNumber("");
      setDoPhoto(null);
      setDoPhotoPreview(null);
      
      // Refresh data
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-in failed");
    } finally {
      setLoading(false);
    }
  };

  // Fetch and view DO photo
  const handleViewPhoto = async (log) => {
    setPhotoLoading(true);
    try {
      const response = await axios.get(`${API}/dumping-logs/${log.id}/photo`);
      if (response.data.photo) {
        setViewingPhoto({
          photo: response.data.photo,
          lorryNumber: log.lorry_number,
          groundName: log.dumping_ground_name,
          checkinTime: log.checkin_time
        });
      } else {
        toast.error("Photo not found");
      }
    } catch (error) {
      toast.error("Failed to load photo");
    } finally {
      setPhotoLoading(false);
    }
  };

  const selectedGround = assignedGrounds.find(g => g.id === selectedGroundId);
  const selectedGroundTotals = todayTotals.find(t => t.ground_id === selectedGroundId);
  
  // Paginated history
  const totalPages = Math.ceil(todayLogs.length / ITEMS_PER_PAGE);
  const paginatedLogs = todayLogs.slice(
    (historyPage - 1) * ITEMS_PER_PAGE,
    historyPage * ITEMS_PER_PAGE
  );

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-[#0d0f12] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0f12] text-white" data-testid="operator-dashboard">
      {/* Header */}
      <header className="bg-[#161920] border-b border-white/10 px-4 py-3 sticky top-0 z-50">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-sm uppercase tracking-wider">
                Dumping Ground
              </h1>
              <p className="text-xs text-white/60">{user?.full_name || user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchDashboardData}
              className="p-2 text-white/60 hover:text-white"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={onLogout}
              className="p-2 text-white/60 hover:text-red-400"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto p-4 pb-24 space-y-4">
        {/* Today's Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          {todayTotals.map((total) => (
            <div
              key={total.ground_id}
              className={`bg-[#161920] border p-4 cursor-pointer transition-all ${
                selectedGroundId === total.ground_id
                  ? 'border-primary bg-primary/10'
                  : 'border-white/10 hover:border-white/30'
              }`}
              onClick={() => setSelectedGroundId(total.ground_id)}
              data-testid={`ground-card-${total.ground_id}`}
            >
              <p className="text-xs text-white/60 uppercase mb-1">{total.ground_name}</p>
              <p className="text-3xl font-bold text-primary font-mono">{total.total_today}</p>
              <p className="text-xs text-white/40">loads today</p>
              {total.checked_in > 0 && (
                <p className="text-xs text-yellow-400 mt-1">
                  {total.checked_in} currently checked in
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Check-in Confirmation - Shows after successful check-in */}
        {activeLog && !showCheckinForm && (
          <div className="bg-green-500/10 border border-green-500/30 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="font-bold text-green-400">CHECK-IN RECORDED</span>
              </div>
              <button
                onClick={() => setActiveLog(null)}
                className="p-1 text-white/40 hover:text-white/60"
                title="Dismiss"
                data-testid="dismiss-checkin-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2">
              <p className="text-xl font-mono font-bold">{activeLog.lorry_number}</p>
              <p className="text-sm text-white/60">{activeLog.dumping_ground_name}</p>
              <p className="text-xs text-white/40">{formatTime(activeLog.checkin_time)}</p>
            </div>
            <button
              onClick={() => setShowCheckinForm(true)}
              className="w-full mt-4 h-12 bg-primary text-white font-heading font-bold uppercase flex items-center justify-center gap-2"
              data-testid="new-checkin-button"
            >
              <Truck className="w-5 h-5" />
              CHECK IN ANOTHER LORRY
            </button>
          </div>
        )}

        {/* Main Screen - Show CHECK IN LORRY button when no active log and form is hidden */}
        {!activeLog && !showCheckinForm && (
          <div className="bg-[#161920] border border-white/10 p-4">
            <button
              onClick={() => setShowCheckinForm(true)}
              className="w-full py-4 bg-primary hover:bg-primary/80 font-bold uppercase flex items-center justify-center gap-2"
              data-testid="open-checkin-button"
            >
              <Truck className="w-5 h-5" />
              CHECK IN LORRY
            </button>
          </div>
        )}

        {/* Check-in Form */}
        {showCheckinForm && (
          <div className="bg-[#161920] border border-white/10 p-4">
            <h2 className="font-heading font-bold uppercase mb-4 flex items-center gap-2">
              <Truck className="w-5 h-5 text-primary" />
              Check In Lorry
            </h2>
            
            <form onSubmit={handleCheckIn} className="space-y-4">
              {/* Dumping Ground Selection */}
              {assignedGrounds.length > 1 && (
                <div>
                  <label className="block text-sm text-white/60 mb-2">Dumping Ground</label>
                  <div className="grid grid-cols-1 gap-2">
                    {assignedGrounds.map((ground) => (
                      <button
                        key={ground.id}
                        type="button"
                        onClick={() => setSelectedGroundId(ground.id)}
                        className={`p-3 text-left border transition-all ${
                          selectedGroundId === ground.id
                            ? 'border-primary bg-primary/20 text-white'
                            : 'border-white/20 bg-transparent text-white/60 hover:border-white/40'
                        }`}
                        data-testid={`select-ground-${ground.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <MapPin className={`w-4 h-4 ${selectedGroundId === ground.id ? 'text-primary' : ''}`} />
                          <span className="font-medium">{ground.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {assignedGrounds.length === 1 && (
                <div className="bg-primary/10 border border-primary/30 p-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span className="font-medium">{assignedGrounds[0].name}</span>
                </div>
              )}

              {/* Lorry Number Input */}
              <div>
                <label className="block text-sm text-white/60 mb-2">Lorry Number</label>
                <input
                  type="text"
                  value={lorryNumber}
                  onChange={(e) => setLorryNumber(e.target.value.toUpperCase())}
                  placeholder="e.g., SGX1234A"
                  className="w-full p-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white placeholder:text-white/30 font-mono text-lg uppercase"
                  data-testid="lorry-number-input"
                />
              </div>

              {/* DO Photo Capture */}
              <div>
                <label className="block text-sm text-white/60 mb-2">Delivery Order Photo</label>
                {doPhotoPreview ? (
                  <div className="relative">
                    <img
                      src={doPhotoPreview}
                      alt="DO Preview"
                      className="w-full h-48 object-cover border border-white/20"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setDoPhoto(null);
                        setDoPhotoPreview(null);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 hover:border-primary/50 cursor-pointer bg-[#1a1d24]">
                    <Camera className="w-8 h-8 text-white/40 mb-2" />
                    <span className="text-sm text-white/40">Tap to capture DO photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoCapture}
                      className="hidden"
                      data-testid="do-photo-input"
                    />
                  </label>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !selectedGroundId || !lorryNumber.trim() || !doPhoto}
                className="w-full py-4 bg-primary hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed font-bold uppercase flex items-center justify-center gap-2"
                data-testid="checkin-button"
              >
                <Truck className="w-5 h-5" />
                {loading ? "Processing..." : "CHECK IN LORRY"}
              </button>

              {/* Cancel Button - Always show, closes form and returns to main screen */}
              <button
                type="button"
                onClick={() => {
                  setLorryNumber("");
                  setDoPhoto(null);
                  setDoPhotoPreview(null);
                  if (assignedGrounds.length > 1) {
                    setSelectedGroundId("");
                  }
                  setShowCheckinForm(false);
                }}
                className="w-full py-3 bg-white/5 hover:bg-white/10 text-white/60 font-bold uppercase border border-white/10"
                data-testid="cancel-checkin-button"
              >
                CANCEL
              </button>
            </form>
          </div>
        )}

        {/* Today's Log History */}
        <div className="bg-[#161920] border border-white/10 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold uppercase flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Today's Logs
            </h2>
            <span className="text-sm text-white/40">{todayLogs.length} entries</span>
          </div>

          {todayLogs.length === 0 ? (
            <p className="text-center text-white/40 py-8">No logs recorded today</p>
          ) : (
            <>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {paginatedLogs.map((log, idx) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 border border-white/10 bg-white/5"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-white/40 w-6">{(historyPage - 1) * ITEMS_PER_PAGE + idx + 1}</span>
                      <div>
                        <p className="font-mono font-bold">{log.lorry_number}</p>
                        <p className="text-xs text-white/40">{log.dumping_ground_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* View DO Photo button */}
                      {log.has_photo && (
                        <button
                          onClick={() => handleViewPhoto(log)}
                          className="p-2 bg-primary/20 text-primary hover:bg-primary/30 rounded transition-colors"
                          title="View DO Photo"
                          data-testid={`view-do-photo-${log.id}`}
                        >
                          <ImageIcon className="w-4 h-4" />
                        </button>
                      )}
                      <div className="text-right">
                        <p className="text-sm">{formatTime(log.checkin_time)}</p>
                        <p className="text-xs text-green-400">Recorded</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-white/10">
                  <button
                    onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                    disabled={historyPage === 1}
                    className="p-2 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm text-white/60">
                    Page {historyPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setHistoryPage(p => Math.min(totalPages, p + 1))}
                    disabled={historyPage === totalPages}
                    className="p-2 disabled:opacity-30"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Photo View Modal */}
      {viewingPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingPhoto(null)}
        >
          <div 
            className="bg-[#161920] border border-white/20 max-w-lg w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h3 className="font-heading font-bold uppercase text-white">Delivery Order Photo</h3>
                <p className="text-sm text-white/60">{viewingPhoto.lorryNumber} • {viewingPhoto.groundName}</p>
                <p className="text-xs text-white/40">{formatDateTimeDMY(viewingPhoto.checkinTime)}</p>
              </div>
              <button
                onClick={() => setViewingPhoto(null)}
                className="p-2 hover:bg-white/10 text-white/60 hover:text-white rounded"
                data-testid="close-photo-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <img 
                src={viewingPhoto.photo} 
                alt="Delivery Order" 
                className="w-full h-auto"
                data-testid="do-photo-image"
              />
            </div>
            <div className="p-4 border-t border-white/10">
              <a
                href={viewingPhoto.photo}
                download={`DO_${viewingPhoto.lorryNumber}_${viewingPhoto.groundName.replace(/\s+/g, '_')}.jpg`}
                className="w-full py-3 bg-primary hover:bg-primary/80 text-white font-bold uppercase flex items-center justify-center gap-2"
                data-testid="download-photo-button"
              >
                <Download className="w-5 h-5" />
                DOWNLOAD PHOTO
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Photo Loading Overlay */}
      {photoLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#161920] p-6 rounded-lg flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="font-heading font-bold text-sm uppercase text-white">Loading Photo...</p>
          </div>
        </div>
      )}
    </div>
  );
}
